import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import { CustomFullWindowOverlay } from '@/components/CustomFullWindowOverlay';
import { Box } from '@/components/lib/components';
import { Modal } from '@/feature/alerts/components/Modal';
import { Toast, ToastProps, toastTypes } from '@/feature/alerts/components/Toast';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { InfoSheet, InfoSheetProps } from './InfoSheet';
import { SelectionItem, SelectionSheet, SelectionSheetProps } from './SelectionSheet';

type OnDismissFn = () => void;

type ToastState = ToastProps & { visible: boolean };

export type ShowInfoSheet = (options: InfoSheetProps & { onDismiss?: OnDismissFn }) => void;

export type Alerts = {
    showInfoSheet: ShowInfoSheet;
    showSelectionSheet: <T extends SelectionItem>(options: SelectionSheetProps<T>) => void;
    showToast: (options: ToastProps, duration?: number) => void;
    clearAllToasts: () => void;
    dismissInfoSheet: () => void;
};

export const AlertsContext = React.createContext<Alerts | undefined>(undefined);

/**
 * Provides access to showing generic info sheets, selection sheets or toasts(//TODO:).
 */
export const AlertsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const infoSheetRef = useRef<BottomSheetModal>(null);
    const infoSheetVisible = useRef<boolean>(false);
    const selectionSheetRef = useRef<BottomSheetModal>(null);
    const [options, setOptions] = useState<InfoSheetProps | undefined>(undefined);
    const [selectionOptions, setSelectionOptions] = useState<SelectionSheetProps<any> | undefined>(undefined);
    const [toastData, setToastData] = useState<ToastState[]>([]);
    const infoOnDismissRef = useRef<OnDismissFn | undefined>(undefined);
    const currentToastTimeoutId = useRef<ReturnType<typeof setTimeout>>(null);

    /* eslint-disable react-hooks/exhaustive-deps */
    const alerts = useMemo(
        () => ({
            showInfoSheet: (o: InfoSheetProps & { onDismiss?: OnDismissFn }) => {
                //dismiss previous info-sheet and show the new one with delay (if any (: )
                infoSheetRef.current?.dismiss({ duration: 100 });
                setTimeout(
                    () => {
                        setOptions(o);
                        infoOnDismissRef.current = o.onDismiss;
                        infoSheetRef.current?.present();
                        infoSheetVisible.current = true;
                    },
                    infoSheetVisible.current ? 150 : 0
                );
            },
            showSelectionSheet: <T extends SelectionItem>(o: SelectionSheetProps<T>) => {
                setSelectionOptions(o);
                selectionSheetRef.current?.present();
            },
            showToast: ({ toastType = toastTypes.SUCCESS, ...props }: ToastProps, duration = 2000) => {
                if (currentToastTimeoutId.current) {
                    clearTimeout(currentToastTimeoutId.current);
                }

                setToastData([...toastData, { toastType, ...props, visible: true }]);

                currentToastTimeoutId.current = setTimeout(() => {
                    setToastData(current => current.map(toast => ({ ...toast, visible: false })));
                }, duration);
            },
            clearAllToasts: () => {
                setToastData([]);
            },
            dismissInfoSheet: () => {
                infoSheetRef.current?.dismiss();
            },
        }),
        []
    );

    const onToastHidden = useCallback(() => setToastData(current => current.filter(toast => toast.visible)), []);

    const toasts = toastData.map(toast => (
        <Toast key={`toast-message-${toast.message}`} {...toast} onHidden={onToastHidden} />
    ));

    return (
        <AlertsContext.Provider value={alerts}>
            <Box flex={1} overflow={'hidden'}>
                {children}
                <Modal id="selection-sheet" modalRef={selectionSheetRef}>
                    {selectionOptions ? <SelectionSheet modalRef={selectionSheetRef} {...selectionOptions} /> : null}
                </Modal>
                <Modal
                    id="info-sheet"
                    modalRef={infoSheetRef}
                    dismissible={options?.dismissible ?? true}
                    onDismiss={() => {
                        infoSheetVisible.current = false;
                        infoOnDismissRef.current?.();
                    }}
                    backgroundColor={options?.backgroundColor}
                >
                    {options ? <InfoSheet modalRef={infoSheetRef} {...options} /> : null}
                </Modal>
                {toasts.length > 0 ? (
                    <CustomFullWindowOverlay unstable_accessibilityContainerViewIsModal={false}>
                        {toasts}
                    </CustomFullWindowOverlay>
                ) : null}
            </Box>
        </AlertsContext.Provider>
    );
};
